/**
 * `probeKindOf` — short closed-set label for a probe path.
 *
 * Lifted out of `views/agents/Probes.tsx` (2026-10-20) so Security · Paths
 * can share the classifier. Pure — same input, same output.
 *
 * Order matters: backups and secrets are tested before WordPress / PHP,
 * so `/wp-config.php.bak` is a backup and `/phpinfo.php` a debug page
 * (founder review 2026-10-20 caught both mislabelled).
 */

export function probeKindOf(path: string): string {
  const p = path.toLowerCase().split('?')[0] ?? '';
  if (/(^|\/)\.\.(\/|$)/.test(p) || /(\.\.%2f|%2e%2e)/.test(p) || /^\/(proc|sys|dev|etc)\//.test(p)) return 'Path escape';
  if (/\.(bak|old|orig|save|swp|sql|tar|tgz|gz|zip|7z|rar|dump)$|~$/.test(p)) return 'Backup file';
  if (/wp-config/.test(p)) return 'Secret file';
  if (/(^|\/)\.(env|git|svn|hg|htaccess|htpasswd|aws|ssh|npmrc|docker|bash_history|vscode|idea)/.test(p)) return 'Secret file';
  if (/\.(pem|key|crt|p12|pfx|kdbx)$|(^|\/)(id_rsa|id_ed25519|credentials|secrets?\.(json|ya?ml))/.test(p)) return 'Secret file';
  if (/(phpinfo|info\.php|test\.php|debug|server-status|server-info|_profiler|actuator|console|telescope)/.test(p)) return 'Debug page';
  if (/\/wp-content\/uploads\/.*\.php/.test(p)) return 'Script in uploads';
  if (/(^|\/)(wp-login\.php|xmlrpc\.php|wp-admin)/.test(p)) return 'WordPress login';
  if (/(shell|cmd|c99|r57|wso|alfa|webadmin)[^/]*\.php$/.test(p)) return 'Webshell';
  if (/(phpmyadmin|myadmin|\/pma\/|adminer)/.test(p)) return 'Database admin';
  if (/\/(admin|administrator|cgi-bin)/.test(p)) return 'Admin page';
  if (/^\/(graphql|graphiql)/.test(p)) return 'GraphQL';
  if (/\.php$/.test(p)) return 'PHP file';
  return 'Probe';
}
